import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import useIsMobile from '../hooks/useIsMobile';
import useGlobalView from '../hooks/useGlobalView';

const ACTION_LABELS = {
  create_participant: 'Création participant',
  update_participant: 'Modification participant',
  delete_participant: 'Suppression participant',
  create_activity: 'Création activité',
  update_activity: 'Modification activité',
  delete_activity: 'Suppression activité',
  record_attendance: 'Présence enregistrée',
  delete_attendance: 'Présence supprimée',
  approve_membership: 'Adhésion approuvée',
  reject_membership: 'Adhésion rejetée',
  promote_participant: 'Promotion automatique',
  login: 'Connexion',
  create_user: 'Création utilisateur',
  delete_user: 'Suppression utilisateur',
  update_user_role: 'Changement de rôle',
  set_groups: 'Modification des groupes',
  set_permissions: 'Modification des permissions',
  role_assignment_denied: "Attribution de rôle refusée",
  access_denied: 'Accès refusé',
  server_error: 'Erreur serveur',
  export_members: 'Export des membres',
  login_failed: 'Échec de connexion',
  import_data: 'Import de données',
  export_audit_log: 'Export du journal',
};

const ACTION_COLORS = {
  create_participant: '#d1fae5',
  update_participant: '#dbeafe',
  delete_participant: '#fee2e2',
  create_activity: '#d1fae5',
  update_activity: '#dbeafe',
  delete_activity: '#fee2e2',
  record_attendance: '#d1fae5',
  delete_attendance: '#fee2e2',
  approve_membership: '#d1fae5',
  reject_membership: '#fee2e2',
  login: '#f3f4f6',
  create_user: '#d1fae5',
  delete_user: '#fee2e2',
  role_assignment_denied: '#fef3c7',
  access_denied: '#fef3c7',
  server_error: '#fee2e2',
  export_members: '#e0e7ff',
  login_failed: '#fee2e2',
  import_data: '#dbeafe',
  export_audit_log: '#e0e7ff',
};

const ACTION_TEXT_COLORS = {
  create_participant: '#065f46',
  update_participant: '#1e40af',
  delete_participant: '#991b1b',
  create_activity: '#065f46',
  update_activity: '#1e40af',
  delete_activity: '#991b1b',
  record_attendance: '#065f46',
  delete_attendance: '#991b1b',
  approve_membership: '#065f46',
  reject_membership: '#991b1b',
  login: '#374151',
  create_user: '#065f46',
  delete_user: '#991b1b',
  role_assignment_denied: '#92400e',
  access_denied: '#92400e',
  server_error: '#991b1b',
  export_members: '#3730a3',
  login_failed: '#991b1b',
  import_data: '#1e40af',
  export_audit_log: '#3730a3',
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

export default function AuditLog() {
  const mob = useIsMobile();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const isGlobal = useGlobalView();
  const pageSize = 25;

  // Mêmes filtres pour la liste et l'export Excel (RCC-EXP-002)
  const filterParams = () => {
    const params = {};
    if (actionFilter) params.action = actionFilter;
    if (userFilter) params.user = userFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return params;
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get('/notifications/audit-logs/export-xlsx/', { params: filterParams(), responseType: 'blob' });
      const match = /filename="([^"]+)"/.exec(res.headers['content-disposition'] || '');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = match ? match[1] : 'RCC_JOURNAL_ACTIVITE.xlsx';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // 403 / 5xx / réseau : déjà signalés par l'intercepteur axios
    } finally {
      setExporting(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, ...filterParams() };
      const res = await api.get('/notifications/audit-logs/', { params });
      setLogs(res.data.results || []);
      setTotal(res.data.count || 0);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, actionFilter, userFilter, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / pageSize);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>
          Historique des modifications
        </h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Toutes les actions importantes enregistrées dans le système
        </p>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', flexDirection: mob ? 'column' : 'row' }}>
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: '0.88rem', minWidth: '200px' }}
        >
          <option value="">Toutes les actions</option>
          {Object.entries(ACTION_LABELS).map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="Rechercher par utilisateur..."
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            style={{ flex: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: '0.88rem' }}
          />
          <button type="submit" style={{ padding: '8px 18px', borderRadius: '8px', background: 'var(--primary)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>
            Filtrer
          </button>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Du</label>
          <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
          <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Au</label>
          <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', fontSize: '0.85rem' }} />
          <button type="button" onClick={exportXlsx} disabled={exporting || total === 0}
            title="Exporte les entrées correspondant aux filtres en cours"
            style={{ padding: '8px 16px', borderRadius: '8px', background: '#eff6ff', color: '#1e40af', border: '1.5px solid #bfdbfe', cursor: exporting || total === 0 ? 'default' : 'pointer', fontWeight: 700, fontSize: '0.85rem', opacity: exporting || total === 0 ? 0.6 : 1 }}>
            {exporting ? 'Export...' : 'Exporter en Excel'}
          </button>
        </div>
      </form>

      {/* Stats bar */}
      <div style={{ marginBottom: '16px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        {loading ? 'Chargement...' : `${total} entrée${total !== 1 ? 's' : ''} trouvée${total !== 1 ? 's' : ''}`}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border-color)', overflowX: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: 'var(--card-shadow)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: mob ? '480px' : 'auto' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid var(--border-color)' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Utilisateur</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action</th>
              {isGlobal && <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Centre</th>}
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Cible</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: '0.82rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={isGlobal ? 6 : 5} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Chargement...</td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={isGlobal ? 6 : 5} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Aucun log trouvé</td>
              </tr>
            ) : (
              logs.map((log, i) => (
                <tr key={log.id} style={{ borderBottom: '1px solid var(--border-color)', background: i % 2 === 0 ? 'transparent' : 'rgba(248,250,252,0.5)' }}>
                  <td style={{ padding: '10px 16px', fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {formatDate(log.created_at)}
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: '0.87rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {log.username || '—'}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '3px 10px',
                      borderRadius: '999px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      background: ACTION_COLORS[log.action] || '#f3f4f6',
                      color: ACTION_TEXT_COLORS[log.action] || '#374151',
                    }}>
                      {log.action_label || ACTION_LABELS[log.action] || log.action}
                    </span>
                    {log.result && log.result !== 'success' && (
                      <span style={{ marginLeft: '6px', fontSize: '0.72rem', fontWeight: 700, color: '#b91c1c' }}>{log.result_label}</span>
                    )}
                  </td>
                  {isGlobal && (
                    <td style={{ padding: '10px 16px', fontSize: '0.82rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {log.church_name || '—'}
                    </td>
                  )}
                  <td style={{ padding: '10px 16px', fontSize: '0.85rem', color: 'var(--text-primary)', maxWidth: '340px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {log.target || '—'}{log.reference && <span style={{ color: 'var(--text-secondary)' }}> · {log.reference}</span>}
                    </div>
                    {(log.old_value || log.new_value) && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        <span style={{ textDecoration: 'line-through' }}>{log.old_value || '—'}</span> → <strong>{log.new_value || '—'}</strong>
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                    {log.ip_address || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '20px' }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1, fontSize: '0.88rem' }}
          >
            Précédent
          </button>
          <span style={{ fontSize: '0.88rem', color: 'var(--text-secondary)' }}>
            Page {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--card-bg)', color: 'var(--text-primary)', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1, fontSize: '0.88rem' }}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
}
